/**
 * Stage screen transition manager
 *
 * State machine:
 *   game ──[click stamp]──► stamp ──[auto 1.5s]──► days
 *   days ──[click back]───► stamp ──[auto 1.5s]──► game
 *
 * Past stages: stamp trigger swaps days content + stamp screen images from
 * pre-rendered <template> elements and data-* attributes — no page reload.
 *
 * Uses CSS classes is-active / is-entering / is-exiting on .js-screen elements.
 */

const STAMP_HOLD_MS = 800;  // how long stamp screen is visible before auto-advancing
const EXIT_MS       = 280;  // duration of js-screen-exit animation (matches SCSS)
const ENTER_MS      = 350;  // duration of enter animation for game/stamp screens
const ENTER_MS_DAYS = 1600; // enter duration for days screen (accounts for card stagger)

let currentScreen      = 'game';
let isTransitioning    = false;
let isPendingPreload   = false; // true while stamp images are being preloaded
let countdownTimer     = null;
let viewingPastStage   = false;

const hud = () => document.getElementById('js-game-hud');

// ── Saved snapshots ───────────────────────────────────────────────────────────
// null key → current stage; 'js-days-tpl-XXX' → that past stage.
// A single origCardsHtml would be overwritten whenever a game is completed on a
// past stage, clobbering the current-stage snapshot (and vice-versa).

let cardSnapshots   = new Map(); // tplId|null → innerHTML string
let currentTplId    = null;      // which stage is in .js-days-cards right now
let origStageBg     = null; // plain URL string, without url('...') — for #js-stage-bg
let origStampBig    = null;
let origStampSmall  = null;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getScreen(name) {
    return document.querySelector(`.js-screen[data-screen="${name}"]`);
}

/** Strip url('...') wrapper from a CSS background-image value. */
function stripCssUrl(val) {
    return (val || '').replace(/^url\(['"]?|['"]?\)$/g, '');
}

/** Save the initial state of the stage bg, stamp screen, and days cards. */
function saveOriginals() {
    const cardsWrap = document.querySelector('.js-days-cards');
    if (cardsWrap) cardSnapshots.set(null, cardsWrap.innerHTML);

    const bgEl = document.getElementById('js-stage-bg');
    if (bgEl) origStageBg = stripCssUrl(bgEl.style.backgroundImage);

    const bigEl = document.querySelector('.s-stage-stamp__big-stamp img');
    if (bigEl) origStampBig = bigEl.src;

    const smallEl = document.querySelector('.s-stage-stamp__stamp-small img');
    if (smallEl) origStampSmall = smallEl.src;
}

/**
 * Re-snapshot the current days-cards HTML after a daily game is completed so that
 * the stamp round-trip (swapDaysCards) restores the card's .is-done state without
 * a page refresh. The snapshot is keyed by currentTplId so past-stage completions
 * don't overwrite the current-stage snapshot (and vice-versa).
 */
function refreshDaysSnapshot() {
    const cardsWrap = document.querySelector('.js-days-cards');
    if (cardsWrap) cardSnapshots.set(currentTplId, cardsWrap.innerHTML);
}

/** Swap the days cards container content from a saved snapshot or <template>. */
function swapDaysCards(tplId) {
    const cardsWrap = document.querySelector('.js-days-cards');
    if (!cardsWrap) return;

    currentTplId = tplId ?? null;

    if (tplId) {
        if (cardSnapshots.has(tplId)) {
            // Restore snapshot — preserves any runtime .is-done state from this session.
            cardsWrap.innerHTML = cardSnapshots.get(tplId);
        } else {
            // First visit to this past stage: clone template and seed the snapshot.
            const tpl = document.getElementById(tplId);
            if (tpl) {
                cardsWrap.innerHTML = '';
                cardsWrap.appendChild(tpl.content.cloneNode(true));
                cardSnapshots.set(tplId, cardsWrap.innerHTML);
            }
        }
    } else {
        cardsWrap.innerHTML = cardSnapshots.get(null) ?? '';
    }
}

/**
 * Update the stage bg + stamp screen images, then resolve only once the new
 * images are fully decoded and ready to paint. Pass null values to restore
 * originals. Awaiting decode (not just network preload) guarantees the <img>
 * elements never paint their previous stamp while the stamp screen becomes
 * visible — the old badge can otherwise flash through for a frame or two.
 * Races a 2 s timeout so a stalled image never blocks the transition forever.
 */
function updateStampScreen(stageBg, big, small) {
    const tasks = [];

    // Background: preload first, swap the style only once the bytes are in.
    const bgUrl = stageBg ?? origStageBg;
    const bgEl  = document.getElementById('js-stage-bg');
    if (bgEl && bgUrl) {
        tasks.push(new Promise((resolve) => {
            const pre = new Image();
            pre.onload = pre.onerror = resolve;
            pre.src = bgUrl;
        }).then(() => { bgEl.style.backgroundImage = `url('${bgUrl}')`; }));
    }

    const bigEl = document.querySelector('.s-stage-stamp__big-stamp img');
    if (bigEl) {
        bigEl.src = big ?? origStampBig ?? '';
        if (bigEl.decode) tasks.push(bigEl.decode().catch(() => {}));
    }

    const smallEl = document.querySelector('.s-stage-stamp__stamp-small img');
    if (smallEl) {
        smallEl.src = small ?? origStampSmall ?? '';
        if (smallEl.decode) tasks.push(smallEl.decode().catch(() => {}));
    }

    const timeout = new Promise((resolve) => setTimeout(resolve, 2000));
    return Promise.race([Promise.all(tasks), timeout]);
}

/**
 * Transition from current screen to `to`.
 * Returns a Promise that resolves when the enter animation completes.
 */
function go(to) {
    return new Promise((resolve) => {
        if (isTransitioning || to === currentScreen) { resolve(); return; }
        isTransitioning = true;

        const fromEl = getScreen(currentScreen);
        const toEl   = getScreen(to);
        if (!fromEl || !toEl) { isTransitioning = false; resolve(); return; }

        // Prepare destination (set anim delays for days screen)
        const enterMs = to === 'days' ? prepareStagger(toEl) : ENTER_MS;

        fromEl.classList.add('is-exiting');

        setTimeout(() => {
            fromEl.classList.remove('is-active', 'is-exiting');
            fromEl.setAttribute('aria-hidden', 'true');

            toEl.classList.add('is-active', 'is-entering');
            toEl.setAttribute('aria-hidden', 'false');
            currentScreen = to;

            // Toggle HUD visibility — hidden on days and welcome screens
            hud()?.classList.toggle('is-hidden', to === 'days' || to === 'welcome');

            if (to === 'days') {
                startCountdownTimers();
            } else {
                stopCountdownTimers();
            }

            setTimeout(() => {
                toEl.classList.remove('is-entering');
                isTransitioning = false;
                resolve();
            }, enterMs);
        }, EXIT_MS);
    });
}

/**
 * Set CSS --anim-delay on each animated element from its data-anim-delay attribute.
 * Schedules instant d-none-style toggle for completed card images.
 * Returns the total enter duration (max delay + animation buffer) for go().
 */
function prepareStagger(daysEl, enterDelay = EXIT_MS) {
    let maxDelay = 0;
    const completedSwitches = [];

    // Reset any previous completed-visible states
    daysEl.querySelectorAll('.is-completed-visible').forEach((el) => {
        el.classList.remove('is-completed-visible');
    });

    daysEl.querySelectorAll('[data-anim-delay]').forEach((el) => {
        const delay = parseInt(el.dataset.animDelay, 10) || 0;
        el.style.setProperty('--anim-delay', `${delay}ms`);
        if (delay > maxDelay) maxDelay = delay;

        if (el.classList.contains('s-stage-days__card-img--completed')) {
            completedSwitches.push({ el, delay });
        }
    });

    // Schedule instant switch (d-none → d-block) for each completed image,
    // but only for cards that are server-side marked .is-done — other cards
    // contain the img only so runtime completion (markCardDone) can reveal it.
    // enterDelay is when is-entering is added relative to now (EXIT_MS for the
    // stamp→days transition, 0 for an in-place replay); it must match so the flip
    // stays in lockstep with the CSS-driven reveals (cards/tags/decor).
    completedSwitches.forEach(({ el, delay }) => {
        const card = el.closest('.js-day-card');
        if (!card || !card.classList.contains('is-done')) return;
        setTimeout(() => {
            card.classList.add('is-completed-visible');
        }, enterDelay + delay);
    });

    return maxDelay + 500;
}

function isPastStage() {
    return viewingPastStage;
}

/**
 * Replay the days-screen reveal (stagger + completed-card flip) while already on
 * the days screen — used right after finishing a daily game, where the game modal
 * was only an overlay (currentScreen stayed 'days', so go('days') is a no-op and
 * never re-runs prepareStagger). Re-runs the same reveal the stamp→days path uses.
 *
 * swapDaysCards replaces the innerHTML from the current snapshot (which was just
 * updated by refreshDaysSnapshot in markCardDone). Fresh DOM nodes are required so
 * the CSS entrance animation (s-stage-days-appear) re-triggers reliably — browsers
 * often skip re-running an animation when the computed property values haven't
 * changed on an existing element.
 */
function replayDaysReveal() {
    const daysEl = getScreen('days');
    if (!daysEl || currentScreen !== 'days') return;

    swapDaysCards(currentTplId);

    // is-entering is added immediately here (no exit phase), so tell prepareStagger
    // the flip offset is 0 — otherwise the completed-photo flip fires EXIT_MS late
    // and the decor (CSS animation-delay) appears before it.
    const enterMs = prepareStagger(daysEl, 0);
    daysEl.classList.add('is-active', 'is-entering');

    // Lock navigation for the reveal's duration, mirroring go(). Without this the
    // back button (and stamp triggers) stay clickable mid-reveal, letting the user
    // leave while the days screen finishes animating behind the stamp/case screen.
    isTransitioning = true;
    setTimeout(() => {
        daysEl.classList.remove('is-entering');
        isTransitioning = false;
    }, enterMs);
}

// ── Countdown timers ───────────────────────────────────────────────────────────

function stopCountdownTimers() {
    if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
    }
}

function startCountdownTimers() {
    stopCountdownTimers();
    updateTimers();
    countdownTimer = setInterval(updateTimers, 1000);
}

function updateTimers() {
    const now = Date.now();
    document.querySelectorAll('.js-stage-timer').forEach((el) => {
        const unlockStr = el.dataset.unlock;
        if (!unlockStr) return;

        const unlockTime = new Date(unlockStr).getTime();
        const diff       = unlockTime - now;

        if (diff <= 0) {
            el.textContent = '00:00:00';
            el.classList.remove('is-urgent');
            return;
        }

        const h   = Math.floor(diff / 3_600_000);
        const m   = Math.floor((diff % 3_600_000) / 60_000);
        const s   = Math.floor((diff % 60_000) / 1_000);
        const pad = (n) => String(n).padStart(2, '0');

        el.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
        el.classList.toggle('is-urgent', diff < 3_600_000);
    });
}

// ── Init ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', saveOriginals);

// ── Event handlers ────────────────────────────────────────────────────────────

// Click on any stamp badge → swap content if needed → game → stamp → days
document.addEventListener('click', (e) => {
    if (!e.target.closest('.js-stamp-trigger')) return;
    if (isTransitioning || isPendingPreload) return;

    const trigger = e.target.closest('.js-stamp-trigger');
    const tplId   = trigger.dataset.daysTpl ?? null;
    const stageBg = trigger.dataset.stageBg  ?? null;
    const big     = trigger.dataset.stampBig ?? null;
    const small   = trigger.dataset.stampSmall ?? null;

    viewingPastStage = (tplId !== null);
    swapDaysCards(tplId);

    // Swap + decode the new stamp/bg BEFORE the screen becomes visible, so only
    // the just-clicked stamp is ever painted — never the previous one.
    isPendingPreload = true;
    updateStampScreen(stageBg, big, small).then(() => {
        isPendingPreload = false;
        go('stamp').then(() => {
            setTimeout(() => go('days'), STAMP_HOLD_MS);
        });
    });
});

// Keyboard (Enter/Space) on stamp trigger for accessibility
document.addEventListener('keydown', (e) => {
    if (!e.target.closest('.js-stamp-trigger')) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.target.closest('.js-stamp-trigger').click();
});

// Click on back button → days → stamp → game
document.addEventListener('click', (e) => {
    if (!e.target.closest('.js-back-to-game')) return;
    if (isTransitioning) return;

    viewingPastStage = false;
    go('stamp').then(() => {
        setTimeout(() => go('game'), STAMP_HOLD_MS);
    });
});

export { go, isPastStage, replayDaysReveal, refreshDaysSnapshot };
